import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { sellerRequestsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Store, CheckCircle, XCircle, Eye, Building, Phone, Calendar } from 'lucide-react';

const SellerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await sellerRequestsAPI.getAll();
      setRequests(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch seller requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await sellerRequestsAPI.update(requestId, { status });
      toast.success(`Request ${status} successfully`);
      fetchRequests(); // Refresh the list
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error(`Failed to ${status} request`);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'secondary', color: 'bg-amber-100 text-amber-800', icon: null },
      approved: { variant: 'success', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { variant: 'destructive', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        {Icon && <Icon className="w-3 h-3 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading seller requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Seller Requests</h1>
          <p className="text-muted-foreground">Review and manage seller access requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-amber-50 text-amber-700">
            {requests.filter(r => r.status === 'pending').length} Pending
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="w-5 h-5" />
            All Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <Store className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No seller requests found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>GST Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{request.user_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {request.user_phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-muted-foreground" />
                        {request.business_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {request.gst_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(request.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Dialog open={showDetailsDialog && selectedRequest?.id === request.id} 
                                onOpenChange={(open) => {
                                  setShowDetailsDialog(open);
                                  if (!open) setSelectedRequest(null);
                                }}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedRequest(request)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <Store className="w-5 h-5" />
                                Seller Request Details
                              </DialogTitle>
                            </DialogHeader>
                            
                            {selectedRequest && (
                              <div className="space-y-6">
                                {/* User Information */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg">User Information</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-3">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Name</label>
                                        <p className="font-medium">{selectedRequest.user_name}</p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Phone</label>
                                        <p className="font-medium">{selectedRequest.user_phone}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>

                                {/* Business Information */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg">Business Information</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-3">
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Business Name</label>
                                      <p className="font-medium">{selectedRequest.business_name}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">GST Number</label>
                                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded inline-block">
                                        {selectedRequest.gst_number}
                                      </p>
                                    </div>
                                  </CardContent>
                                </Card>

                                {/* GST Verification Status */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                      <CheckCircle className="w-5 h-5 text-green-500" />
                                      GST Verification Status
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg">
                                      <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                        <span className="font-medium text-green-800 dark:text-green-200">
                                          GST Number Verified & Active
                                        </span>
                                      </div>
                                      <p className="text-sm text-green-700 dark:text-green-300">
                                        This GST number has been verified through the government GST portal and is currently active.
                                      </p>
                                    </div>
                                  </CardContent>
                                </Card>

                                {/* Request Status */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="text-lg">Request Status</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <span>Current Status:</span>
                                      {getStatusBadge(selectedRequest.status)}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span>Submitted:</span>
                                      <span className="text-sm text-muted-foreground">
                                        {formatDate(selectedRequest.created_at)}
                                      </span>
                                    </div>
                                  </CardContent>
                                </Card>

                                {/* Actions */}
                                {selectedRequest.status === 'pending' && (
                                  <div className="flex gap-3 pt-4 border-t">
                                    <Button 
                                      onClick={() => handleUpdateStatus(selectedRequest.id, 'approved')}
                                      className="flex-1 bg-green-600 hover:bg-green-700"
                                    >
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Approve Request
                                    </Button>
                                    <Button 
                                      variant="destructive"
                                      onClick={() => handleUpdateStatus(selectedRequest.id, 'rejected')}
                                      className="flex-1"
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Reject Request
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateStatus(request.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleUpdateStatus(request.id, 'rejected')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerRequests;
